import { Component, Input, OnInit } from '@angular/core';
import { DEFAULT_DATE_TIME_FORMAT } from 'src/app/core/ccomx-date-formats';
import { CrewMemberChangeAction } from 'src/app/crew-management-user/enums/crew-member-change-action.enum';
import { PaxOverviewType } from 'src/app/crew-management-user/enums/pax-overview-type.enum';
import {
  TripVersionReason,
  TripVersionReasonLabels,
} from 'src/app/crew-management-user/enums/trip-version-reason.enum';
import {
  ICrewMemberChange,
  ITripLogFieldChange,
} from 'src/app/crew-management-user/models/trip.model';
import { ILogChangeBreakdownItem } from 'src/app/shared/main-table-module/main-table-module-specific-template/table-module-templates/cells/log-change-breakdown/log-change-breakdown.model';
import { PersonOnBoardType } from '../../../../../crew-management-user/models/current-personnel/personnel-with-missing-confirmation.model';

const CREW_MEMBER_CHANGES_FIELD_NAME = 'Crew member changes';
const PAX_OVERVIEW_VERSION_REASONS = new Set<TripVersionReason>([
  TripVersionReason.PaxOverviewsAdded,
  TripVersionReason.PaxOverviewsUpdated,
  TripVersionReason.PaxOverviewsDeleted,
]);

interface ICrewMemberLogChangeGroup {
  name: string;
  actionKey: string;
  changes: ILogChangeBreakdownItem[];
  from: string | null;
  to: string;
}

@Component({
  selector: 'app-crew-management-user-template',
  templateUrl: './crew-management-user-template.component.html',
  styleUrls: ['./crew-management-user-template.component.scss'],
  standalone: false,
})
export class CrewManagementUserTemplateComponent implements OnInit {
  @Input() columnName: string;
  @Input() element: any;

  paxOverviewType = PaxOverviewType;
  personOnBoardType = PersonOnBoardType;
  tripVersionReason = TripVersionReason;
  tripVersionReasonLabels: Record<number, string> = TripVersionReasonLabels;
  private readonly crewMemberChangeActionLabels: Record<number, string> = {
    [CrewMemberChangeAction.Added]: 'added',
    [CrewMemberChangeAction.Updated]: 'updated',
    [CrewMemberChangeAction.Removed]: 'removed',
  };
  private readonly crewMemberFieldLabels: Record<string, string> = {
    Position: 'position',
    From: 'startDate',
    To: 'endDate',
    Comment: 'comment',
  };
  dateTimeFormat = DEFAULT_DATE_TIME_FORMAT;

  crewMemberChangeGroups: ICrewMemberLogChangeGroup[] = [];
  fieldChangesWithTranslations: Array<ITripLogFieldChange & { translationKey: string }> = [];
  private readonly removeTranslationKeys = ['Attachments added', 'Attachments removed'];

  ngOnInit(): void {
    this.crewMemberChangeGroups = (this.element?.crewMembersChanges ?? []).map(
      (change: ICrewMemberChange) => this.buildCrewMemberChangeGroup(change)
    );
    const hasStructuredCrewChanges = (this.element?.crewMembersChanges || []).some(
      (change: ICrewMemberChange) => !!change.fieldsChanged?.length
    );
    this.fieldChangesWithTranslations = (this.element?.fieldsChanged || [])
      .filter(
        (fieldChange: ITripLogFieldChange) =>
          !hasStructuredCrewChanges || fieldChange.fieldName !== CREW_MEMBER_CHANGES_FIELD_NAME
      )
      .map((fieldChange: ITripLogFieldChange) => {
        let translationKey = 'changedTo';
        if (this.removeTranslationKeys.includes(fieldChange.fieldName)) {
          translationKey = '';
        }
        if (this.element.tripVersionReason === TripVersionReason.Created) {
          translationKey = 'setTo';
        }
        return {
          ...fieldChange,
          oldValue:
            this.element.tripVersionReason !== TripVersionReason.Created &&
            ['StartDate', 'EndDate'].includes(fieldChange.fieldName)
              ? fieldChange.oldValue
              : undefined,
          translationKey,
        };
      });
  }

  private buildCrewMemberChangeGroup(change: ICrewMemberChange): ICrewMemberLogChangeGroup {
    return {
      name: change.name,
      actionKey: this.isPaxOverviewChange()
        ? ''
        : (this.crewMemberChangeActionLabels[change.action] ?? ''),
      changes: (change.fieldsChanged ?? []).map((fieldChange) =>
        this.buildCrewMemberFieldChange(fieldChange, change.action)
      ),
      from: change.from,
      to: change.to,
    };
  }

  private buildCrewMemberFieldChange(
    fieldChange: ITripLogFieldChange,
    action: CrewMemberChangeAction
  ): ILogChangeBreakdownItem {
    if (action === CrewMemberChangeAction.Updated && fieldChange.fieldName === 'Comment') {
      return this.buildUpdatedCommentFieldChange(fieldChange);
    }

    const oldValue = action === CrewMemberChangeAction.Updated ? fieldChange.oldValue : undefined;
    let translationKey: string | undefined;
    if (action !== CrewMemberChangeAction.Removed && oldValue === undefined) {
      translationKey = this.hasValue(fieldChange.value) ? 'hasBeenSetTo' : 'hasBeenCleared';
    }

    return {
      fieldName: this.crewMemberFieldLabels[fieldChange.fieldName] ?? fieldChange.fieldName,
      value: fieldChange.value,
      oldValue,
      translationKey,
    };
  }

  private buildUpdatedCommentFieldChange(
    fieldChange: ITripLogFieldChange
  ): ILogChangeBreakdownItem {
    const hasComment = this.hasValue(fieldChange.value);
    const hadComment = this.hasValue(fieldChange.oldValue);
    let translationKey = 'hasBeenChanged';

    if (!hasComment) {
      translationKey = hadComment ? 'hasBeenClearedWithPreviousValue' : 'hasBeenCleared';
    } else if (!hadComment) {
      translationKey = 'hasBeenSetTo';
    }

    return {
      fieldName: this.crewMemberFieldLabels[fieldChange.fieldName],
      value: hasComment && !hadComment ? fieldChange.value : null,
      oldValue: !hasComment && hadComment ? fieldChange.oldValue : undefined,
      translationKey,
    };
  }

  private hasValue(value?: string | null): value is string {
    return value !== null && value !== undefined && value !== '';
  }

  private isPaxOverviewChange(): boolean {
    return PAX_OVERVIEW_VERSION_REASONS.has(this.element?.tripVersionReason);
  }
}
